import React, { useEffect, useState } from 'react';
import { useParams, useHistory, Link, useLocation } from 'react-router-dom';
import * as AppPics from 'assets/images';
import {
  AppTextField,
  AppUploadBlock,
  AppButton,
  AppRadioBox,
  AppSwitchBox,
  AppSelectBox,
} from 'components';
import Joi from 'joi';
import { notifier } from 'components/Toaster';
import { checkForErrors } from 'utilities/fns';
import useManagePlayable from 'hooks/API/POST/useManagePlayable';
import useGetOnePlayable from 'hooks/API/GET/useGetOnePlayable';
import usePlayables from 'hooks/API/GET/usePlayables';
import useDebounce from 'hooks/useDebounce';
import AppBreadcrumbs from 'components/molecules/AppBreadcrumbs';
import BasicDelete from 'components/molecules/Modal/Delete/BasicDelete';

const actionsConfig = {
  open: false,
  data: {},
  Header: null,
  Body: null,
  isDeleteOperation: false,
};

function ManagePlayable() {
  const { podcastId, episodeId, playableId } = useParams();
  const history = useHistory();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const redirectBack = params.get('redirect_back');
  const isEditMode = Boolean(playableId && playableId !== 'add');
  const [playableMode, setPlayableMode] = useState('create_new');
  const [selectedExistingPlayable, setSelectedExistingPlayable] = useState(null);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [actions, setActions] = useState({ ...actionsConfig });

  const debouncedSearch = useDebounce(searchKeyword, 500);

  const handleGoBack = () => {
    if (redirectBack) {
      history.replace(decodeURIComponent(redirectBack));
    } else {
      history.goBack();
    }
  };

  const [formData, setFormData] = useState({
    playableName: '',
    playableSubtitle: '',
    playableArtists: '',
    playableDuration: '',
    url: '',
    fullPath: '',
    podcastStreamingLink: '',
    podcastClosedCaptionsFileLocation: '',
    podcastPlayableCoverImage: '',
    podcastPlayableFeatured: false,
    trialAccessible: false,
    playableUrl: '',
    readSize: '',
    playService: 'aws',
    isParent: false,
    canPublish: null,
  });
  const [formErrors, setFormErrors] = useState([]);
  const [validationErrorServer, setValidationErrorServer] = useState({});
  const [playableUploadObject, setUploadedData] = useState(null);
  const [playableUploadObjectCover, setUploadedDataForImage] = useState(null);
  const [podcastCaptionsFullPath, setPodcastCaptionsFullPath] = useState(null);
  const [disableSubmit, setDisableSubmit] = useState(false);
  const [deletedCaptionsFile, setDeletedCaptionsFile] = useState(false);
  const [deletedCoverImage, setDeletedCoverImage] = useState(false);
  const [deletedPlayFile, setDeletedPlayFile] = useState(false);
  const [playableImageFullPath, setPlayableImageFullPath] = useState(null);
  const [useParentArtist, setUseParentArtist] = useState(false);
  const [cachedArtist, setCachedArtist] = useState('');
  const [published, setPublished] = useState(false);
  const [isSaved, setIsSaved] = useState(false);

  const { mutate, isLoading } = useManagePlayable({ podcastId, episodeId, playableId });
  const getPlayable = useGetOnePlayable({
    enabled: isEditMode,
    podcastId,
    episodeId,
    playableId,
  });
  const { data: existingPlayablesData, isLoading: isLoadingPlayables } = usePlayables({
    podcastId,
    episodeId,
    enabled: playableMode === 'choose_existing' && !isEditMode,
    configs: {
      params: {
        // page: 0,
        // limit: 20,
        keyword: debouncedSearch,
        mode: 'dropdown',
        listAllPlayables: true,
      },
    },
  });

  // const existingPlayables = existingPlayablesData?.result || [];
  const existingPlayables =
    existingPlayablesData?.result || existingPlayablesData?.data || existingPlayablesData || [];

  useEffect(() => {
    if (isEditMode && getPlayable?.data?.data?.playableArtists) {
      setCachedArtist(getPlayable.data.data.playableArtists);
    }
  }, [isEditMode, getPlayable?.data]);

  useEffect(() => {
    if (playableId === 'add') {
      clearData();
      return;
    }
    if (isEditMode && getPlayable?.data?.data) {
      const currentData = getPlayable.data.data;
      setFormData({
        playableName: currentData.playableName || '',
        playableSubtitle: currentData.playableSubtitle || '',
        playableArtists: currentData.playableArtists || '',
        playableDuration: currentData.playableDuration || '',
        url: currentData.url || '',
        fullPath: currentData.fullPath || '',
        podcastStreamingLink: currentData.podcastStreamingLink || '',
        podcastClosedCaptionsFileLocation: currentData.podcastClosedCaptionsFileLocation || '',
        podcastPlayableCoverImage: currentData.podcastPlayableCoverImage || '',
        podcastPlayableCoverImageFullPath: currentData.PlayableCoverFullPath || '',
        playableUrl: currentData.playableURL || '',
        readSize: currentData.readSize || '',
        podcastPlayableFeatured: currentData.podcastPlayableFeatured || false,
        trialAccessible: currentData.trialAccessible || false,
        playService: currentData.playService || '',
        canPublish: currentData.isPublished || null,
      });
      setPodcastCaptionsFullPath(currentData.podcastClosedCaptionsFullFileLocation || '');
      setPublished(currentData.isPublished);
      setPlayableImageFullPath(currentData.PlayableCoverFullPath);
      setIsSaved(true);
    }

    if (redirectBack && window.history.length <= 2) {
      window.history.replaceState(null, '', location.pathname + location.search);
      window.history.pushState(null, '', decodeURIComponent(redirectBack));
      window.history.pushState(null, '', location.pathname + location.search);
    }

    if (isEditMode && getPlayable?.data?.data) {
      const playableData = getPlayable.data.data;
      setFormData({
        ...playableData,
        playableUrl: playableData.playableURL || playableData.playableUrl || '',
        podcastPlayableCoverImageFullPath:
          playableData.PlayableCoverFullPath ||
          playableData.podcastPlayableCoverImageFullPath ||
          '',
        canEdit: playableData.canEdit,
        canPublish: playableData.isPublished || null,
      });
      setPodcastCaptionsFullPath(playableData.podcastClosedCaptionsFullFileLocation);

      if (playableData.PlayableCoverFullPath || playableData.podcastPlayableCoverImageFullPath) {
        setUploadedDataForImage({
          fullUrl:
            playableData.PlayableCoverFullPath || playableData.podcastPlayableCoverImageFullPath,
        });
      }
      setDeletedCaptionsFile(false);
      setDeletedCoverImage(false);
      setDeletedPlayFile(false);
    }
  }, [playableId, isEditMode, getPlayable?.data, redirectBack]);
  const handleInputChange = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const handlePlayableModeChange = (mode) => {
    setPlayableMode(mode);
    if (mode === 'create_new') {
      setSelectedExistingPlayable(null);
      setSearchKeyword('');
      setShowDropdown(false);
    } else {
      clearData();
    }
  };
  const handleSwitchKeyDown = (field) => (e) => {
    // Handle Space and Enter keys to toggle the switch
    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault(); // Prevent default behavior
      handleInputChange(field, !formData[field]);
    }
  };

  const handleExistingPlayableSelect = (playable) => {
    if (playable.PlayableCoverFullPath || playable.podcastPlayableCoverImageFullPath) {
      setUploadedDataForImage({
        fullUrl: playable.PlayableCoverFullPath || playable.podcastPlayableCoverImageFullPath,
      });
    }
    setSelectedExistingPlayable(playable);
    setFormData({
      playableName: playable.name || '',
      playableSubtitle: playable.subtitle || '',
      playableArtists: playable.artists || '',
      playableDuration: playable.duration || '',
      url: playable.url || '',
      fullPath: playable.fullPath || '',
      podcastStreamingLink: playable.podcastStreamingLink || '',
      podcastClosedCaptionsFileLocation: playable.podcastClosedCaptionsFileLocation || '',
      podcastPlayableCoverImage: playable.podcastPlayableCoverImage || '',
      podcastPlayableFeatured: playable.podcastPlayableFeatured || false,
      trialAccessible: playable.trialAccessible || false,
      playableUrl: playable.playableURL || '',
      readSize: playable.readSize || '',
      canEdit: true,
      podcastPlayableCoverImageFullPath:
        playable.PlayableCoverFullPath || playable.podcastPlayableCoverImageFullPath || '',
      playService: 'aws',
      canPublish: playable.isPublished || null,
    });
    setShowDropdown(false);
    setSearchKeyword(playable.name || '');
  };

  const handleSearchKeywordChange = (value) => {
    setSearchKeyword(value);
    setShowDropdown(value.length > 0);
    if (value === '') {
      setSelectedExistingPlayable(null);
    }
  };

  const handleCaptionsFileChange = ({ uploadedData }) => {
    const fullPath = uploadedData.basePath + uploadedData.file;
    setPodcastCaptionsFullPath(fullPath);
    setFormData((prev) => ({
      ...prev,
      podcastClosedCaptionsFileLocation: uploadedData.file,
    }));
    setDeletedCaptionsFile(false);
  };

  const handleImageChange = ({ uploadedData }) => {
    const filePathOnly = uploadedData.file;
    setUploadedDataForImage({ ...uploadedData, fullUrl: filePathOnly });
    setUploadedDataForImage(uploadedData);
    setPlayableImageFullPath(uploadedData.basePath + uploadedData.file);
    setFormData((prev) => ({
      ...prev,
      podcastPlayableCoverImage: uploadedData.file,
      podcastPlayableCoverImageFullPath: filePathOnly,
    }));
    setDeletedCoverImage(false);
  };

  const handlePlayItem = ({ uploadedData }) => {
    uploadedData.fullPath = uploadedData.basePath + uploadedData.file;
    setUploadedData(uploadedData);
    setFormData((prev) => ({
      ...prev,
      readSize: uploadedData.size,
      url: uploadedData.file,
    }));
    setDeletedPlayFile(false);
  };
  const handleDeleteCaptionsFile = () => {
    setPodcastCaptionsFullPath(null);
    setFormData((prev) => ({
      ...prev,
      podcastClosedCaptionsFileLocation: null,
    }));
    setDeletedCaptionsFile(true);
    notifier.success('Captions file deleted successfully');
  };

  const handleDeleteCoverImage = () => {
    setUploadedDataForImage(null);
    setFormData((prev) => ({
      ...prev,
      podcastPlayableCoverImage: null,
      podcastPlayableCoverImageFullPath: null,
    }));
    setDeletedCoverImage(true);
    notifier.success('Cover image deleted successfully');
  };

  const handleDeletePlayFile = () => {
    setUploadedData(null);
    setFormData((prev) => ({
      ...prev,
      url: '',
      fullPath: '',
      readSize: '',
    }));
    setDeletedPlayFile(true);
    notifier.success('Play file deleted successfully');
  };

  const handlePublishToggleConfirm = (newValue) => {
    setActions({
      open: true,
      Header: 'Confirm Publish Status',
      Body: `Are you sure you want to ${newValue ? 'publish' : 'unpublish'} this play file?`,
      isDeleteOperation: false,
      data: {
        type: 'PUBLISH_TOGGLE',
        value: newValue,
      },
    });
  };

  const openDeleteConfirm = (type, header, body) => {
    setActions({
      open: true,
      Header: header,
      Body: body,
      isDeleteOperation: true,
      data: { type },
    });
  };

  const handleDeleteConfirm = () => {
    const { type, value } = actions.data || {};

    switch (type) {
      case 'CAPTIONS_FILE':
        handleDeleteCaptionsFile();
        break;

      case 'COVER_IMAGE':
        handleDeleteCoverImage();
        break;

      case 'PLAY_FILE':
        handleDeletePlayFile();
        break;

      case 'PUBLISH_TOGGLE':
        handlePublish(value);
        break;

      default:
        break;
    }

    setActions({ ...actionsConfig });
  };

  /**
   * Builds the request body shared by Save and Publish/Unpublish.
   *
   * Why: the edit (PUT) API reads `coverImage` and `closedCaptionsFileLocation`, while the
   * form state stores them as `podcastPlayableCoverImage` / `podcastClosedCaptionsFileLocation`.
   * Every write to that endpoint must go through this mapping, otherwise the server receives
   * `undefined` for those fields and wipes them (this is what dropped the cover image on publish).
   *
   * @param {Object} [overrides] - Extra fields merged last (e.g. `{ canPublish: true }`).
   * @returns {Object} Payload ready to send to the create/update playable API.
   */
  const buildPlayablePayload = (overrides = {}) => ({
    ...formData,
    closedCaptionsFileLocation: deletedCaptionsFile
      ? ''
      : formData.podcastClosedCaptionsFileLocation || '',
    podcastEpisode: episodeId,
    readSize: parseInt(formData.readSize) || 0,
    ...(isEditMode
      ? { coverImage: deletedCoverImage ? '' : formData.podcastPlayableCoverImage || '' }
      : {
          podcastPlayableCoverImage: deletedCoverImage ? '' : formData.podcastPlayableCoverImage,
        }),
    url: deletedPlayFile ? '' : formData.url,
    ...overrides,
  });

  const handleSave = () => {
    setFormErrors([]);
    setValidationErrorServer({});

    let dataToSubmit = buildPlayablePayload();
    if (playableMode === 'choose_existing' && selectedExistingPlayable) {
      dataToSubmit.existingPlayableId = selectedExistingPlayable._id;
    }

    const validation = playableSchema.validate(dataToSubmit, {
      abortEarly: false,
      allowUnknown: true,
    });

    if (validation?.error) {
      setFormErrors(validation.error?.details ?? []);
      return;
    }
    mutate(dataToSubmit, {
      onSuccess: (response) => {
        let message;
        if (playableMode === 'choose_existing' && !isEditMode) {
          const playableName = selectedExistingPlayable?.name || 'Playable';
          message = `${playableName} added to episode! Opening details for customization...`;
        } else {
          message = response?.data?.data?.message || response?.data?.message;
        }
        notifier.success(message);
        setIsSaved(true);

        setTimeout(() => {
          if (playableMode === 'choose_existing' && !isEditMode) {
            const createdPlayableId =
              response?.data?.record ||
              response?.data?.data?.playableId ||
              response?.data?.playableId ||
              selectedExistingPlayable?._id;

            if (createdPlayableId) {
              const redirectPath = `/podcast/${podcastId}/episode/${episodeId}/playable/${createdPlayableId}`;

              history.replace(redirectPath);
            } else {
              handleGoBack();
            }
          } else {
            // handleGoBack();
          }
        }, 2000);
        if (!isEditMode) {
          clearData();
        }
      },
      onError: (error) => {
        const message = error?.response?.data?.message;
        const validationErrors = error?.response?.data?.errors;
        notifier.error(message);
        setValidationErrorServer(validationErrors || {});
      },
    });
  };

  const handlePublish = (value) => {
    setFormErrors([]);
    if (disableSubmit) return;

    // Reuse the Save payload so cover image / captions survive the publish toggle.
    const dataToSubmit = buildPlayablePayload({ canPublish: value });

    mutate(dataToSubmit, {
      onError(failData) {
        const errorData = failData?.response?.data;
        const errorMessage = errorData?.message;
        const validationErrors = errorData?.errors;

        setValidationErrorServer(validationErrors || {});
        notifier.error(errorMessage);
      },

      onSuccess() {
        notifier.success(`Play File ${value ? 'published' : 'unpublished'} successfully`);
        setTimeout(() => {
          handleGoBack();
        }, 2000);
      },
    });
  };

  const clearData = () => {
    setFormData({
      playableName: '',
      playableSubtitle: '',
      playableArtists: '',
      playableDuration: '',
      url: '',
      fullPath: '',
      podcastStreamingLink: '',
      podcastClosedCaptionsFileLocation: '',
      podcastPlayableCoverImage: '',
      podcastPlayableFeatured: false,
      trialAccessible: false,
      playableUrl: '',
      readSize: '',
      playService: 'aws',
      canPublish: null,
    });
    setUploadedData(null);
    setUploadedDataForImage(null);
    setPodcastCaptionsFullPath(null);
    setFormErrors([]);
    setValidationErrorServer({});
    setDeletedCaptionsFile(false);
    setDeletedCoverImage(false);
    setDeletedPlayFile(false);
    setSelectedExistingPlayable(null);
    setSearchKeyword('');
    setShowDropdown(false);
  };

  const errors = checkForErrors(formErrors);

  return (
    <>
      <BasicDelete
        open={actions.open}
        closeModal={() => setActions({ ...actionsConfig })}
        Header={actions.Header}
        Body={actions.Body}
        handleConfirmation={handleDeleteConfirm}
      />
      <div className="mainWraper coursetabsection">
        <div className="tab">
          <ul className="tab__menu">
            <li className="back">
              <button className="btn-back" onClick={handleGoBack}>
                <img src={AppPics.BackSvg} alt="Back" />
              </button>
            </li>
          </ul>
          <div
            className="tab__content
        ___adjust___tab___padding"
          >
            <div className="tab__content-actHead">
              <AppBreadcrumbs
                links={[
                  { label: 'Dashboard', to: '/dashboard' },
                  { label: 'Podcast Home', to: '/podcast' },
                  { label: 'Podcasts', to: '/podcast/list' },
                  { label: 'Podcast Episodes', to: `/podcast/${podcastId}/episodes` },
                  {
                    label: 'Podcast Play file',
                    to: `/podcast/${podcastId}/episode/${episodeId}/playables`,
                  },
                ]}
                current={isEditMode ? 'Update Play File' : 'Add Play File'}
              />
            </div>

            {/* <div className="mainWraper"> */}
            <div className="tab__content-actBody">
              <div className="px-5 py-5">
                <div className="category-form">
                  <div className="row mt-4">
                    <div className="col-lg-12">
                      <h2 className="prime-head p-0 boldtitle">
                        {isEditMode ? 'Update Play file' : 'Add Play file'}
                      </h2>
                      {validationErrorServer && Object.keys(validationErrorServer).length > 0 && (
                        <ul className="error-list">
                          {Object.values(validationErrorServer).map((err, idx) => (
                            <li key={idx}>{err.msg}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                  {!isEditMode && (
                    <div className="row mt-4">
                      <div className="col-lg-12">
                        <label className="label d-block mb-3">Play file Source</label>
                        <div className="d-flex gap-4">
                          <AppRadioBox
                            inputProps={{
                              name: 'playableMode',
                              value: 'create_new',
                              checked: playableMode === 'create_new',
                              onChange: () => handlePlayableModeChange('create_new'),
                            }}
                            labelProps={{ label: 'Create New Play file' }}
                          />
                          <AppRadioBox
                            inputProps={{
                              name: 'playableMode',
                              value: 'choose_existing',
                              checked: playableMode === 'choose_existing',
                              onChange: () => handlePlayableModeChange('choose_existing'),
                            }}
                            labelProps={{ label: 'Choose from Existing' }}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                  {playableMode === 'choose_existing' && !isEditMode && (
                    <div className="row mt-4">
                      <div className="col-lg-6">
                        <label className="label d-block">Search Existing Play file</label>
                        <div className="position-relative">
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Type to search play file..."
                            value={searchKeyword}
                            onChange={(e) => handleSearchKeywordChange(e.target.value)}
                            onFocus={() => setShowDropdown(searchKeyword.length > 0)}
                          />

                          {showDropdown && (
                            <div
                              className="dropdown-menu show position-absolute w-100"
                              style={{ maxHeight: '300px', overflowY: 'auto', zIndex: 1000 }}
                            >
                              {isLoadingPlayables ? (
                                <div className="dropdown-item text-center">Loading...</div>
                              ) : existingPlayables.length > 0 ? (
                                existingPlayables.map((playable) => (
                                  <button
                                    key={playable._id}
                                    className="dropdown-item"
                                    onClick={() => handleExistingPlayableSelect(playable)}
                                  >
                                    <div>
                                      <strong>{playable.name}</strong>
                                      {playable.subtitle && (
                                        <div className="text-muted small">{playable.subtitle}</div>
                                      )}
                                      {playable.artists && (
                                        <div className="text-muted small">
                                          By: {playable.artists}
                                        </div>
                                      )}
                                      {playable.duration && (
                                        <div className="text-muted small">
                                          Duration: {playable.duration}
                                        </div>
                                      )}
                                    </div>
                                  </button>
                                ))
                              ) : (
                                <div className="dropdown-item text-center text-muted">
                                  {searchKeyword
                                    ? `No playables found for "${searchKeyword}"`
                                    : 'Start typing to search'}
                                </div>
                              )}
                            </div>
                          )}
                        </div>

                        {selectedExistingPlayable && (
                          <div className="mt-2 p-3 bg-light rounded">
                            <strong>Selected: {selectedExistingPlayable.name}</strong>
                            {selectedExistingPlayable.subtitle && (
                              <div className="text-muted">{selectedExistingPlayable.subtitle}</div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  {(playableMode === 'create_new' || isEditMode) && (
                    <>
                      <div className="row mt-4">
                        <div className="col-lg-6">
                          <AppTextField
                            labelProps={{ label: 'Play file Name *' }}
                            inputProps={{
                              id: 'playableName',
                              name: 'playableName',
                              type: 'text',
                              placeholder: 'Play file Name',
                              value: formData.playableName,
                              onChange: (e) => handleInputChange('playableName', e.target.value),
                              readOnly:
                                playableMode === 'choose_existing' && selectedExistingPlayable,
                              tabIndex: 1,
                            }}
                            fieldErrors={errors['playableName']}
                          />
                          {errors.playableName && (
                            <div className="text-danger">{errors.playableName.errorMessage}</div>
                          )}
                        </div>
                        <div className="col-lg-6">
                          <AppTextField
                            labelProps={{ label: 'Subtitle' }}
                            inputProps={{
                              id: 'playableSubtitle',
                              name: 'playableSubtitle',
                              type: 'text',
                              placeholder: 'Subtitle',
                              value: formData.playableSubtitle,
                              onChange: (e) =>
                                handleInputChange('playableSubtitle', e.target.value),
                              readOnly:
                                playableMode === 'choose_existing' && selectedExistingPlayable,
                              tabIndex: 2,
                            }}
                            fieldErrors={errors['playableSubtitle']}
                          />
                        </div>
                      </div>
                      <div className="row mt-4">
                        <div className="col-lg-6 d-flex flex-row align-items-center">
                          <div className={isEditMode && 'w-100'}>
                            <AppTextField
                              labelProps={{ label: 'Artists' }}
                              inputProps={{
                                id: 'playableArtists',
                                name: 'playableArtists',
                                type: 'text',
                                placeholder: 'Artists',
                                value: formData.playableArtists,
                                onChange: (e) =>
                                  handleInputChange('playableArtists', e.target.value),
                                disabled: formData.isParent,
                                readOnly:
                                  playableMode === 'choose_existing' && selectedExistingPlayable,
                                tabIndex: 3,
                              }}
                              fieldErrors={errors['playableArtists']}
                            />
                          </div>
                          {!isEditMode && (
                            <div className="ml-4 d-flex flex-column">
                              <label className="form-label invisible">Placeholder</label>
                              <AppSwitchBox
                                inputProps={{
                                  checked: formData.isParent,
                                  onChange: (e) => {
                                    const checked = e.target.checked;

                                    setFormData((prev) => ({
                                      ...prev,
                                      isParent: checked,
                                      playableArtists: checked ? '' : prev.playableArtists,
                                    }));
                                  },
                                  disabled:
                                    playableMode === 'choose_existing' && selectedExistingPlayable,
                                  tabIndex: 4,
                                  'aria-label': 'Toggle parent artist',
                                }}
                                labelProps={{ label: 'Choose parent Artist' }}
                              />
                            </div>
                          )}
                        </div>
                        <div className="col-lg-6">
                          <AppTextField
                            labelProps={{ label: 'Play file URL' }}
                            inputProps={{
                              id: 'playableUrl',
                              name: 'playableUrl',
                              type: 'text',
                              placeholder: 'Play file URL',
                              value: formData.playableUrl,
                              onChange: (e) => handleInputChange('playableUrl', e.target.value),
                              readOnly:
                                playableMode === 'choose_existing' && selectedExistingPlayable,
                              tabIndex: 5,
                            }}
                            fieldErrors={errors['playableUrl']}
                          />
                        </div>
                      </div>
                    </>
                  )}
                  <div className="row mt-4">
                    {(playableMode === 'create_new' || isEditMode) && (
                      <>
                        <div className="col-lg-3 col-md-6 col-sm-12">
                          <AppUploadBlock
                            labelProps={{ label: `Captions File Upload` }}
                            props={{
                              referenceKey: 'PODCAST_PLAYABLE',
                              isImage: true,
                              isPublic: true,
                              uploadCallback: (val) => setDisableSubmit(val),
                              handleChange: handleCaptionsFileChange,
                              fileData: {
                                fullPath: podcastCaptionsFullPath
                                  ? podcastCaptionsFullPath
                                  : formData?.podcastClosedCaptionsFileLocation?.startsWith('http')
                                  ? formData.podcastClosedCaptionsFileLocation
                                  : formData.podcastClosedCaptionsFileLocation
                                  ? `https://s3.amazonaws.com/cdn.edpodia.co.uk${formData.podcastClosedCaptionsFileLocation}`
                                  : '',
                              },
                              tabIndex: 7,
                              disabled: isEditMode && formData.canEdit === false,
                            }}
                          />
                          {(podcastCaptionsFullPath ||
                            formData?.podcastClosedCaptionsFileLocation) && (
                            <button
                              className="actions-icons mx-2 text-danger mt-2"
                              title="Delete Captions File"
                              onClick={() =>
                                openDeleteConfirm(
                                  'CAPTIONS_FILE',
                                  'Delete Captions File',
                                  'Are you sure you want to delete this captions file?'
                                )
                              }
                              type="button"
                              disabled={isEditMode && formData.canEdit === false}
                            >
                              <img src={AppPics.DeleteSvg} alt="Delete" />
                            </button>
                          )}
                        </div>
                        <div className="col-lg-3 col-md-6 col-sm-12">
                          <AppUploadBlock
                            labelProps={{ label: `Cover Image` }}
                            props={{
                              referenceKey: 'PODCAST_PLAYABLE',
                              isImage: true,
                              isPublic: true,
                              uploadCallback: (val) => setDisableSubmit(val),
                              handleChange: handleImageChange,
                              fileData: {
                                fullPath: deletedCoverImage ? null : playableImageFullPath,
                              },
                              clearFile: deletedCoverImage,
                              tabIndex: 8,
                            }}
                          />
                          {(playableUploadObjectCover?.fullUrl ||
                            formData?.podcastPlayableCoverImageFullPath) && (
                            <button
                              className="actions-icons mx-2 text-danger mt-2"
                              title="Delete Cover Image"
                              onClick={() =>
                                openDeleteConfirm(
                                  'COVER_IMAGE',
                                  'Delete Cover Image',
                                  'Are you sure you want to delete this cover image?'
                                )
                              }
                              type="button"
                            >
                              <img src={AppPics.DeleteSvg} alt="Delete" />
                            </button>
                          )}
                        </div>
                        <div className="col-lg-3 col-md-6 col-sm-12">
                          <AppUploadBlock
                            labelProps={{ label: `Play file *` }}
                            props={{
                              referenceKey: 'PODCAST_URI',
                              isImage: false,
                              isAudio: true,
                              isPublic: true,
                              uploadCallback: (val) => setDisableSubmit(val),
                              handleChange: handlePlayItem,
                              fileData: {
                                fullPath: playableUploadObject
                                  ? playableUploadObject?.fullPath
                                  : formData?.rawFilePlayFullPath,
                              },
                              tabIndex: 9,
                              disabled: isEditMode && formData.canEdit === false,
                            }}
                            fieldErrors={errors['url']}
                          />
                          {errors.url && (
                            <div className="text-danger">{errors.url.errorMessage}</div>
                          )}
                          {(playableUploadObject?.fullPath || formData?.rawFilePlayFullPath) && (
                            <button
                              className="actions-icons mx-2 text-danger mt-2"
                              title="Delete Play File"
                              onClick={() =>
                                openDeleteConfirm(
                                  'PLAY_FILE',
                                  'Delete Play File',
                                  'Are you sure you want to delete this play file?'
                                )
                              }
                              type="button"
                              disabled={isEditMode && formData.canEdit === false}
                            >
                              <img src={AppPics.DeleteSvg} alt="Delete" />
                            </button>
                          )}
                        </div>
                      </>
                    )}
                    <div
                      className={`${
                        playableMode === 'choose_existing'
                          ? 'col-lg-6'
                          : 'col-lg-3 col-md-6 col-sm-12'
                      } `}
                    >
                      <AppSelectBox
                        labelProps={{ label: 'Choose Service *' }}
                        selectProps={{
                          value: formData.playService,
                          onChange: (event) =>
                            setFormData((prev) => ({
                              ...prev,
                              playService: event.target.value,
                            })),
                        }}
                      >
                        <option value="aws">AWS</option>
                        <option value="bunny">Bunny</option>
                      </AppSelectBox>
                    </div>
                  </div>
                  {(playableMode === 'create_new' || isEditMode) && (
                    <div className="row mt-4">
                      <div className="col-lg-6">
                        <label className="label d-block">
                          Featured?
                          <span>
                            <small className="text-muted">
                              {' '}
                              (Featured Play files appear in the Featured Play files section on the
                              Dashboard page.)
                            </small>
                          </span>
                        </label>
                        <div className="d-flex flex-row">
                          <AppSwitchBox
                            inputProps={{
                              checked: formData.podcastPlayableFeatured,
                              onChange: (e) =>
                                handleInputChange('podcastPlayableFeatured', e.target.checked),
                              onKeyDown: handleSwitchKeyDown('podcastPlayableFeatured'),
                              disabled:
                                playableMode === 'choose_existing' && selectedExistingPlayable,
                              tabIndex: 10,
                              'aria-label': 'Toggle featured playable status',
                            }}
                            labelProps={{ label: 'Show as Featured Play file' }}
                          />
                        </div>
                      </div>
                      <div className="col-lg-6">
                        <label className="label d-block">
                          Free in trial?
                          <span>
                            <small className="text-muted">
                              {' '}
                              (Users in their free trial can play only the files marked free.)
                            </small>
                          </span>
                        </label>
                        <div className="d-flex flex-row">
                          <AppSwitchBox
                            inputProps={{
                              checked: formData.trialAccessible,
                              onChange: (e) =>
                                handleInputChange('trialAccessible', e.target.checked),
                              onKeyDown: handleSwitchKeyDown('trialAccessible'),
                              disabled:
                                playableMode === 'choose_existing' && selectedExistingPlayable,
                              tabIndex: 11,
                              'aria-label': 'Toggle free in trial status',
                            }}
                            labelProps={{ label: 'Free for trial users' }}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                  <div className="d-flex justify-content-end mt-4">
                    <AppButton
                      type="submit"
                      variantClass="btn-primary mx-3"
                      style={{ minWidth: '1.5rem' }}
                      onClick={() => handlePublishToggleConfirm(!published)}
                      disabled={!isSaved || isLoading}
                      tabIndex={6}
                    >
                      {published ? 'Unpublish Play File' : 'Publish Play File'}
                    </AppButton>
                    <AppButton
                      type="submit"
                      variantClass="btn-primary mx-3"
                      style={{ minWidth: '1.5rem' }}
                      onClick={handleSave}
                      disabled={
                        disableSubmit ||
                        isLoading ||
                        (playableMode === 'choose_existing' &&
                          !selectedExistingPlayable &&
                          !isEditMode)
                      }
                      tabIndex={11}
                    >
                      {isLoading ? 'Saving...' : 'Save Play file'}
                    </AppButton>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

const playableSchema = Joi.object({
  playableName: Joi.string().trim().min(1).max(100).required().messages({
    'string.base': 'Playable name must be a string.',
    'string.empty': 'Playable name cannot be empty.',
    'string.min': 'Playable name must be at least 1 character long.',
    'string.max': 'Playable name must be at most 100 characters long.',
    'any.required': 'Playable name is required.',
  }),

  url: Joi.string().required().messages({
    'string.base': 'Audio URL must be a string.',
    'string.empty': 'Audio URL cannot be empty.',
    'any.required': 'Audio URL is required.',
  }),

  readSize: Joi.number().integer().min(0).required().messages({
    'number.base': 'Read size must be a number.',
    'number.min': 'Read size cannot be negative.',
    'any.required': 'Read size is required.',
  }),
});

export default ManagePlayable;
